import { Immutable } from "immer";
import React from "react";

import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { IProduct } from "../../../models/catalogue";
import { IProductCategoryID } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { useIsMobileBreakpoint } from "../../../utils/detectMobile";
import { GridContent } from "../GridContent";
import { SideNav } from "../SideNav";
import { TopNav } from "../TopNav";
import { GridFilterConfig } from "../filters";
import { startHistoryListener } from "../history";
import { GridSortPlacement, GridVariant } from "../models";
import { selectProducts } from "../selectors";
import { GridSorterConfig } from "../sorters";
import { loadProducts } from "../thunks";
import { BuildGridTile } from "../tiles";

import styles from "./index.module.scss";

/**
 * What the grid has already worked out, for chrome it does not draw itself.
 *
 * Everything here is a decision the grid owns: an embedder that re-derived any
 * of it would hold a second copy that drifts silently the next time the rule
 * changes.
 */
type IGridChromeContext = Immutable<{
    /**
     * Every configured filter, in snippet order.
     *
     * Not narrowed to the sidebar's subset -- `SideNav` narrows for itself,
     * because it is the thing that draws the accordions. Chrome that draws its
     * own list should narrow the same way, on `showInSidebar`.
     */
    filters: readonly GridFilterConfig[];
    /**
     * The sorters this grid offers, or empty when it offers none.
     *
     * Resolved against the snippet's `hide_sort` only, so a caller cannot show
     * a sort the CMS switched off.
     *
     * `sortPlacement` is deliberately not applied: chrome in this slot may be
     * the only sort control at viewports where `SideNav` is not rendered, so
     * narrowing here would take the sort away from a phone on exactly the
     * grids that asked for a sidebar sort. Chrome that also draws at desktop
     * width has to suppress itself there -- this context carries no
     * `isMobile` to branch on, by design, so that is the caller's own
     * breakpoint to own.
     */
    sorters: readonly GridSorterConfig[];
}>;

/**
 * Content rendered directly above the tiles, in the same column and at the
 * same width -- not above the whole grid, which would span the sidebar too.
 *
 * A function, not a node, so chrome rendered here is handed the same derived
 * values the grid's own chrome answers to. An embedder given only a slot has
 * to rebuild them from the snippet's raw strings, and every rule it rebuilds
 * is one the lib can change without a compile error: which filters belong in
 * a sidebar, whether the sort is hidden, how the config JSON decodes. See
 * `IGridChromeContext`.
 *
 * Called during `ProductGrid`'s render, which is after `ProductGridSSR`'s
 * prefill `useMemo` has run -- so the SSR ordering the slot was introduced for
 * still holds. Drawn while the tiles load, too: the spinner replaces the tiles
 * only.
 */
type GridHeaderSlot = (context: IGridChromeContext) => React.ReactNode;

/**
 * Whether the grid draws its own nav, and the chrome that replaces it if not.
 *
 * A union rather than two optional props, because only two of the four
 * combinations are usable and the broken one fails silently: `TopNav` holds
 * the applied-filter chips on every viewport and the Filter and Sort controls
 * on a small one, and those controls are the only route into the filters
 * there is. Dropping it without putting something back leaves a phone with
 * tiles it cannot narrow or reorder -- no error, no empty state, just a grid
 * that does nothing. Requiring `header` alongside `hideNav` makes that a
 * compile error instead of a bug report.
 *
 * Opt-out because every grid that predates this one uses `TopNav`.
 */
export type INavProps =
    | { hideNav?: false; header?: GridHeaderSlot }
    | { hideNav: true; header: GridHeaderSlot };

/**
 * Re-derives the pairing above so a pass-through layer can forward it.
 *
 * Every layer between the CMS block and this component has to do this, because
 * TypeScript will not carry a discriminated union member-wise once it has been
 * intersected into a prop bag: reading `hideNav` and `header` off that bag
 * independently widens them back to the four combinations the union exists to
 * rule out, and the spread is then rejected. The ternary re-establishes the
 * discriminant, which is the only way back.
 *
 * Written once here, beside the contract it restates, rather than at each
 * layer. Two layers already forward it; a copy that drifted from the union --
 * a second required member alongside `header`, say -- would narrow to
 * something silently wrong rather than fail to compile.
 */
export const narrowNavProps = (props: INavProps): INavProps =>
    props.hideNav
        ? { hideNav: true, header: props.header }
        : { hideNav: false, header: props.header };

type IProps = Immutable<{
    gridVariant: GridVariant;
    productCategoryID: IProductCategoryID;
    filters: readonly GridFilterConfig[];
    sorters?: readonly GridSorterConfig[];
    hideSort?: boolean;
    /**
     * Where the sort control goes. Defaults to the shared dropdown above the
     * tiles; `"sidebar"` moves the same sorters into `SideNav` instead. The
     * two are exclusive, so a grid never shows both.
     */
    sortPlacement?: GridSortPlacement;
    disableHoverOverlay?: boolean;
}> &
    INavProps & {
        buildExternalCallout?: (rootProduct: IProduct) => React.ReactNode;
        /**
         * See `BuildGridTile`. Decides per product whether `ProductCard` renders;
         * the `ProductCard`-only props above still configure it wherever the tile
         * falls back to `renderDefault`.
         */
        buildTile?: BuildGridTile;
    };

export const ProductGrid = (props: IProps): React.ReactNode => {
    const dispatch = useAppDispatch();
    const isLoading = useAppSelector((state) => state.productgrid2.isLoading);
    const products = useAppSelector(selectProducts);
    const isMobile = useIsMobileBreakpoint();

    React.useEffect(() => {
        startHistoryListener(dispatch);
    }, [dispatch]);

    React.useEffect(() => {
        // Skip API fetch if products were pre-filled by SSR
        if (products.length > 0) return;
        void dispatch(loadProducts(props.productCategoryID));
    }, [dispatch, props.productCategoryID, products.length]);

    /**
     * Where the sort ends up, resolved from the two things that decide it.
     *
     * `hideSort` is the snippet's field and `sortPlacement` is the embedding
     * page's, and read separately they can disagree -- an editor who ticks
     * Hide Sort on a grid whose page asked for a sidebar sort should still
     * get no sort. Deriving one value here means both `TopNav` and `SideNav`
     * answer to the same decision instead of to a different half of it.
     */
    const sortLocation = props.hideSort
        ? "none"
        : props.hideNav
          ? // `"top"` names `TopNav`'s dropdown, and `hideNav` has just taken
            // `TopNav` away -- so for such a grid the sidebar is the only
            // desktop home a sort has, and `sortPlacement` cannot name a
            // surface that no longer exists. Left as the default and this
            // grid would have no sort control at any desktop width: no
            // `TopNav`, no sorters passed to `SideNav`, and a `header` slot
            // whose contract tells it to suppress itself above mobile.
            //
            // Folded here rather than required on `INavProps` the way `header`
            // is: saying it in the union means carrying `sortPlacement`
            // through every pass-through layer and `narrowNavProps` with it,
            // and this is not a preference a caller should be asked to state
            // twice -- `"top"` is not a place this grid has.
            "sidebar"
          : (props.sortPlacement ?? "top");
    const sortsInSidebar = sortLocation === "sidebar";

    /**
     * The chrome stays put while the tiles load; only the tiles are replaced.
     *
     * The spinner used to stand in for the whole grid, which meant a load also
     * took away the sort control, the applied-filter chips and Clear All --
     * and on a small viewport those are the only route into any of it there
     * is. That is not a hypothetical state: the SSR prefill dispatches an
     * empty product list when a payload fails to decode or every variant in
     * the category is unpurchasable, and the mount effect below then fetches,
     * so the grid can sit in it for a whole round trip.
     *
     * What stays is what is usable with no products loaded: sorters come from
     * config, and `filterState`/`sortMethod` are already in the store. The
     * attribute-derived filters are not -- their options come from the product
     * list this state has emptied -- so `GridFilter` draws nothing rather than
     * an accordion that opens onto nothing. Chrome staying mounted is not a
     * promise that every control in it is populated.
     */
    const content = (
        <>
            {!props.hideNav && (
                <TopNav
                    gridVariant={props.gridVariant}
                    // The whole list, not the sidebar's narrowed one:
                    // this is also where a filter's removal chip and
                    // Clear All come from, and both have to cover every
                    // filter that is on -- including one a pill applied
                    // that has no accordion. `SideNav` narrows for
                    // itself.
                    filters={props.filters}
                    sorters={props.sorters}
                    // Hidden here only where the sidebar actually shows it.
                    // The design gives a small viewport its own Sort
                    // control beside Filter rather than a section inside
                    // the filter drawer, and that control is this
                    // dropdown -- so moving the sort is a desktop-column
                    // decision, and suppressing it on mobile too would
                    // leave no way to sort at all.
                    hideSort={
                        sortLocation === "none" || (sortsInSidebar && !isMobile)
                    }
                    isMobile={isMobile}
                />
            )}
            <div className={styles.contentContainer}>
                {!isMobile && (
                    <SideNav
                        gridVariant={props.gridVariant}
                        filters={props.filters}
                        sorters={sortsInSidebar ? props.sorters : undefined}
                        isMobile={isMobile}
                    />
                )}
                <div className={styles.mainColumn}>
                    {props.header?.({
                        filters: props.filters,
                        sorters:
                            sortLocation === "none"
                                ? []
                                : (props.sorters ?? []),
                    })}
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <GridContent
                            gridVariant={props.gridVariant}
                            cardType="grid-card"
                            filters={props.filters}
                            sorters={props.sorters}
                            disableHoverOverlay={props.disableHoverOverlay}
                            buildExternalCallout={props.buildExternalCallout}
                            buildTile={props.buildTile}
                        />
                    )}
                </div>
            </div>
        </>
    );
    return <div className={styles.container}>{content}</div>;
};
